import * as React from 'react';
import { Format, formatValue } from './Utils';
import ErrorBlock from './ErrorBlock';
import classnames from 'classnames';
import { Res } from '../../api';
import Button from './Button';
import Downshift, { GetItemPropsOptions } from 'downshift';

interface Props<TOption> {
	availableOptions: TOption[] | ((filter: string, extended: boolean) => Promise<TOption[]>);
	errorMsg: string;
	formatOption: Format;
	onOptionSelected: (option: TOption) => void;
	onShowSearchDialog: () => void;
	placeholder?: string;
	disabled?: boolean;
	minLengthToActivateSearch?: number;
}

interface AutoCompleteTextBoxState<TOption> {
	searchText: string;
	options: TOption[];
}

export default class AutoCompleteTextBox<TOption> extends React.Component<Props<TOption>, AutoCompleteTextBoxState<TOption>> {
	private currentSearch: Nullable<number> = null;
	private input = React.createRef<HTMLInputElement>();

	constructor(props: Props<TOption>) {
		super(props);

		this.onTextChange = this.onTextChange.bind(this);

		this.state = {
			searchText: '',
			options: props.availableOptions instanceof Array ? props.availableOptions || [] : []
		};
	}

	focus() {
		this.input.current!.focus();
	}

	search(text: string): Promise<TOption[]> {
		if (this.props.availableOptions instanceof Array) {
			const isMatch = (option: TOption) => formatValue(option, this.props.formatOption).toLocaleLowerCase().indexOf(text.toLocaleLowerCase()) >= 0;
			return Promise.resolve(text ? this.props.availableOptions.filter(isMatch) : []);
		}
		return this.props.availableOptions(text, false);
	}

	getMinLengthToActivateSearch() {
		return this.props.minLengthToActivateSearch || 3;
	}

	onTextChange(e: React.ChangeEvent<HTMLInputElement>) {

		const value = e.target.value;
		// Si hay una búsqueda en curso, la cancelo
		if (this.currentSearch) {
			window.clearTimeout(this.currentSearch);
			this.currentSearch = null;
		}

		this.setState({ searchText: value, options: [] });

		if (value && value.length >= this.getMinLengthToActivateSearch()) {

			// Espero a que el texto es estabilice antes de lanzar la búsqueda

			const ms = this.props.availableOptions instanceof Array ? 0 : 200;
			this.currentSearch = window.setTimeout(async () => {

				// Ha cambiado el texto antes de poder lanzar la búsqueda
				if (this.state.searchText !== value)
					return;

				const options = await this.search(value);

				// Ha cambiado el texto durante la búsqueda
				if (this.state.searchText !== value)
					return;

				this.setState({ searchText: value, options });
			}, ms);
		}
	}

	render() {

		const shouldItemRender = (item: TOption, value: string): boolean =>
			!!value &&
			value.length >= this.getMinLengthToActivateSearch() &&
			formatValue(item, this.props.formatOption).toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) >= 0;

		const getItemValue = (item: TOption): string => formatValue(item, this.props.formatOption);

		const renderItem = (item: TOption, index: number, isHighlighted: boolean, getItemProps: (options: GetItemPropsOptions<TOption>) => any): React.ReactNode => {
			const value = getItemValue(item);
			return <li
				{...getItemProps({
					key: value, index, item,
					className: classnames({ 'list-group-item': true, 'needsclick': true, 'item-focused ': isHighlighted })
				})} >
				{value}
			</li>;
		};

		const buttonStyle = {
			borderRadius: '0 4px 4px 0'
		};

		return <div>
			<ErrorBlock msg={this.props.errorMsg} />
			<Downshift<TOption>
				onChange={(item: TOption | null, stateAndHelpers) => {
					if (item) {
						this.props.onOptionSelected(item);
						stateAndHelpers.clearSelection();
					}
				}}
				itemToString={(item: TOption | null) => item ? getItemValue(item) : ''}
			>
				{({
					getInputProps,
					getMenuProps,
					isOpen,
					inputValue,
					getRootProps,
					getItemProps,
					highlightedIndex,
				}) => (
					<div style={{ position: 'relative' }}>
						<div className='input-group' {...getRootProps({}, { suppressRefError: true })}>
							<input {...getInputProps({
								disabled: this.props.disabled,
								ref: this.input,
								className: 'form-control',
								placeholder: this.props.placeholder || Res.WA.WA_SEARCH_PLACE_HOLDER(),
								onKeyDown: (e: React.KeyboardEvent) => {
									if (e.key === 'F3') {
										this.props.onShowSearchDialog();
										e.preventDefault();
									}
								},
								onChange: this.onTextChange
							})} />
							<span className='input-group-btn'>
								<Button disabled={this.props.disabled} style={buttonStyle} onClick={this.props.onShowSearchDialog}><i className='text-info fa fa-search'></i></Button>
							</span>
						</div>
						{
							isOpen && inputValue && inputValue.length >= this.getMinLengthToActivateSearch()
								? <ul {...getMenuProps({ className: 'list-group downshift-list' })}>
									{
										this.props.availableOptions instanceof Array ?
											this.props.availableOptions.filter(option => shouldItemRender(option, inputValue))
												.map((option, index) => renderItem(option, index, highlightedIndex === index, getItemProps))
											: this.state.options.map((option, index) => renderItem(option, index, highlightedIndex === index, getItemProps))
									}
								</ul>
								: null
						}
					</div>
				)}
			</Downshift>

		</div>;
	}

}

export type AutoCompleteTextBoxCtor<T> = new (props: Props<T>) => AutoCompleteTextBox<T>;
